import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CheckRounded as CheckIcon,
  // SLICE B — ✗ is the REJECT, per Grant's "check/x icon for approve / reject".
  // It is free to mean that only because Slice A moved the old Kill button to a
  // labelled "Review" with the note icon: ✗ never sat on a re-render request.
  CloseRounded as RejectIcon,
  DownloadRounded as DownloadIcon,
  EditNoteRounded as ReviewIcon,
  // PKT-0612 — "Mark as posted" records a manual post; it never publishes.
  TaskAltRounded as PostedIcon,
  PlayArrowRounded as PlayIcon,
  VolumeOffRounded as MutedIcon,
  VolumeUpRounded as UnmutedIcon,
} from "@mui/icons-material";
import { TOUCH_TARGET } from "../../theme/theme";
import {
  contentReviewService,
  VERDICT_ELIGIBLE_STATUSES,
  type ContentCommandTruth,
  type ContentGridVerdict,
  type ContentReviewItem,
} from "../../services/contentReviewService";
import { beatLines } from "./sceneBeats";

interface Props {
  item: ContentReviewItem;
  mobile: boolean;
  /** Mobile reel: the feed names exactly one card active; only it plays. */
  active?: boolean;
  busy: boolean;
  downloading?: boolean;
  onApprove: (item: ContentReviewItem) => void;
  /**
   * SLICE B — the ✗. Same shape as `onApprove` on purpose: no note parameter,
   * because "H" runs zero operations and words attached to it are discarded.
   */
  onReject: (item: ContentReviewItem) => void;
  onSendBack: (item: ContentReviewItem) => void;
  onDownload: (item: ContentReviewItem) => void;
  /**
   * PKT-0612 — opens the "Mark as posted" sheet for an APPROVED reel. Records
   * a manual post in the ledger (migration 091); posting stays manual by
   * ruling and nothing on this card can publish.
   */
  onMarkPosted?: (item: ContentReviewItem) => void;
  unknownVerdict?: ContentGridVerdict | null;
  onRetry: (item: ContentReviewItem) => void;
}

const humanStatus = (status: string) => status.replaceAll("_", " ");

/**
 * 🔴 PROOF-0473 DEFECT 2 — WHICH COMMAND STATES STILL LEAVE THE CARD DECIDABLE.
 *
 * `content_verdict_commands.package_id` is UNIQUE (053:120): a package has ONE
 * command slot. The card used to disarm on the mere PRESENCE of a command
 * (`!item.command`), which was a faithful reading of the database — until two
 * migrations changed what the database will accept:
 *
 *   080 (APPLIED IN PRODUCTION 2026-08-18) — `refused` and `exhausted` RELEASE
 *     the slot. A refusal is the box saying "I understood and correctly
 *     declined"; a second, different dial is exactly the right next move, and
 *     the enqueue RPC now answers `queued` for it. Grant's card showed
 *     "Not made — R-NO-ELIGIBLE-COPY" with the full reason and NOTHING TO CLICK.
 *
 *   082 (⚠️ NOT APPLIED AS THIS SHIPS — see the deploy note below) — an
 *     `applied` K-* RELEASES the slot too, because a revision request is not a
 *     decision: the box re-renders, republishes the card as `revised` with
 *     `verdict='pending'`, and the card is asking to be decided. Without this
 *     you can never approve a video you asked to be revised.
 *
 * ⚠️ DEPLOY ORDER: APPLY 082 BEFORE DEPLOYING THIS BUILD. The two halves of the
 *   `applied` K-* case are independent in one direction only. Migration without
 *   the build: nothing re-arms, no change. Build without the migration: the
 *   re-armed K-* card answers `conflict`, and the chip says "Decision conflict
 *   — nothing changed" — honest and recoverable on refresh, but it is a worse
 *   card than the one it replaces, and it is avoidable by ordering. The
 *   `refused`/`exhausted` half has no such coupling: 080 is already applied.
 *
 * 🔑 WHAT STAYS DISARMED, AND WHY: `queued`, `leased` and `retry` — the box may
 *   still act on them — and an `applied` P or H, which is a COMPLETED DECISION
 *   the database keeps final in both migrations. `conflict` is not a stored
 *   state; it is a return value, and a card carrying one has already lost its
 *   race. This function is the ONE place that opinion lives, so the screen and
 *   the enqueue RPC cannot drift apart silently.
 */
/** An ACK precedes child publication. Legacy in-place revisions have no child reference. */
export const commandWaitingForChild = (command?: ContentCommandTruth | null): boolean =>
  !!command && command.state === "applied" && command.verdict.startsWith("K-")
  && !!command.child_package_id && command.child_ready !== true;

export const commandSlotReleased = (
  command?: ContentCommandTruth | null,
): boolean => {
  if (!command) return false;
  if (command.state === "refused" || command.state === "exhausted") return true;
  return command.state === "applied" && command.verdict.startsWith("K-") && !commandWaitingForChild(command);
};


/**
 * PKT-0527 — the Rejected corpus, named honestly. `qa_failed` and `corrupt`
 * have been served end to end for weeks and were simply unrenderable; `parked`
 * is what the box writes for a terminal hold. None of them is a delete.
 */
const CORPUS_LABELS: Record<string, { label: string; help: string }> = {
  qa_failed: {
    label: "Failed QA",
    help: "The engine's own checks rejected this one. You can still act on it.",
  },
  corrupt: {
    label: "Corrupt",
    help: "The package failed its seal check — the files do not match the manifest.",
  },
  parked: {
    label: "Parked",
    help: "Held by the engine. Kept here to learn from, never deleted.",
  },
};

/**
 * PKT-0427 — a re-render request on a package the box CANNOT revise is a real,
 * saved decision whose re-render will never run. `053:527` retries it to
 * `attempt_count >= 20` and then lands `exhausted`.
 *
 * So the two generic labels are both untrue for it: "Queued — waiting for
 * Content engine" says something is coming (nothing is), and "Couldn’t apply —
 * Admin review needed" sends Grant to an Admin who has nothing to do. The part
 * that IS true is that the decision and its note were saved — migration 055
 * stores `review_note` on `content_verdict_commands`.
 *
 * 🔄 CORRECTED Ch72 (2026-08-29): this comment used to end "…and never leases it
 * to the box ('The note stays LN Pro-side taste data')." **That is no longer
 * true.** Migration 085 (PKT-L5) added `review_note` to
 * `lease_content_review_commands` as column 14, and it is APPLIED in production
 * (verified by `pg_get_functiondef` on the live database). The running box
 * release maps `command.review_note` off the lease. The note reaches the engine.
 *
 * The CONCLUSION is unchanged and is why this edit is safe: for a NON-REVISABLE
 * package the engine still cannot act — `reviewCockpit` throws `NON-REVISABLE`
 * for any K-* verdict when `revision_capable` is false — so "the decision and
 * its note were saved" remains exactly the true part to say here. Only the
 * REASON was stale.
 */
const isNoteOnlyOutcome = (item: ContentReviewItem): boolean =>
  Boolean(item.command?.verdict.startsWith("K-")) && !item.revision_capable;

/**
 * 🔴 MIGRATION 079 — THE ONE OUTCOME THIS CARD COULD NEVER SAY.
 *
 * A refusal is not a failure and not a success: the box read the verdict,
 * understood it, and correctly declined it. Until 079 there was no state for
 * that, so the worker acked clean and Grant read "Applied" on a card where
 * nothing had been made — while the box's own disk held
 *   stop_code: "R-NO-ELIGIBLE-COPY"
 *   blockers: ["K-COPY found no rewritable on-screen line: every non-CTA scene
 *              is licensed by a verbatim claim, and the approved copy pool may
 *              not overwrite evidence-backed text."]
 *
 * ⇒ THE SENTENCE ALREADY EXISTED AND WAS GOOD. Every layer below this one is
 *   transport; this is the only place it becomes something Grant can read. A
 *   refusal recorded correctly and never displayed is the same defect one layer
 *   down, so the reason is rendered IN FULL and never behind a hover — the reel
 *   feed he actually reviews on is mobile, and mobile has no hover.
 *
 * ⚠️ THIS BRANCH MUST COME BEFORE isNoteOnlyOutcome. A refusal is always a K-*
 *   verdict, and most refusable packages are not revision_capable, so the
 *   note-only branch would otherwise swallow it and say "Saved — re-render
 *   unavailable for this reel": true about the note, and silent about the
 *   engine having a specific, printable objection.
 */
/*
 * THE MACHINE->HUMAN LAYER (Ch62, Grant's ask: "we need to formalize errors").
 *
 * The engine emits a stable CODE. Everything a reviewer READS is written here,
 * in one place, in his language. The engine's own sentence is still shown in
 * full below — see the refusal_reason block, which this does NOT replace — but
 * it is no longer the FIRST thing on the card, because most of the time it
 * answers a question the reviewer was not asking.
 *
 * `kind` is the field the old card was missing entirely:
 *   "refused" — the engine did its job and said no. NOTHING IS WRONG.
 *   "blocked" — something is actually broken and no verdict will help.
 * Those two rendered identically before this change, so a guard protecting the
 * truth-lock looked exactly like a failure.
 *
 * An unknown code is NOT an error here: it falls through to the raw engine text,
 * which is why this map can never make the card less informative than it was.
 */
const REFUSAL_COPY: Record<string, { human: string; kind: "refused" | "blocked" }> = {
  "R-NO-ELIGIBLE-COPY": {
    human: "Nothing here can be reworded — every line is backed by verified property data.",
    kind: "refused",
  },
  "R-NOT-ELIGIBLE": {
    human: "That change belongs to a different tool than the one this note asked for.",
    kind: "refused",
  },
  "R-HOLD": { human: "Held. No re-render was attempted.", kind: "refused" },
  "R-TAMPERED": {
    human: "The request did not match this package and was refused.",
    kind: "blocked",
  },
  PACKAGE_NOT_FOUND: {
    human: "The video is fine — your note was filed against the wrong batch. Re-submit it from this card.",
    kind: "blocked",
  },
  PLAN_MEDIA_MISSING: {
    human: "The clips this reel needs are not on the box yet.",
    kind: "blocked",
  },
  PLAN_LINE_CORE_MISSING: {
    human: "This reel arrived without an authored caption, so it was not built.",
    kind: "blocked",
  },

  /* ─── Added Ch70. Every code below is one the box can actually put in
   * `refusal_code`, taken from the three places that produce it rather than
   * from imagination: revisionEngine.ts's `RevisionStopCode` union (the closed
   * set of engine stop codes), locateNowProContentMirrorClient.ts's
   * PERMANENT_VERDICT_FAILURES (exact-match local failures), and its
   * PERMANENT_VERDICT_FAILURE_PREFIXES (colon codes whose PREFIX goes on the
   * wire and whose suffix is spoken in the reason).
   *
   * 🔴 NON-REVISABLE was the gap that mattered: it is the refusal Grant
   * actually received in production (box journal, 2026-08-26, pkg-6912be68)
   * and it had no reviewer-language headline. ─── */

  /* The engine ran and declined. */
  "R-APPROVED": {
    human: "Filed as an approval, so nothing was changed. If you were asking for an edit, re-file it as a copy or hook change.",
    kind: "refused",
  },
  "R-NO-APPROVED-VARIANT": {
    human: "That line is your own writing, and there is no approved alternative to swap in. Name the wording you want.",
    kind: "refused",
  },
  "R-CAPTION-DESYNC": {
    human: "The caption quotes this line word for word, so the line cannot move without the caption going out of sync.",
    kind: "refused",
  },
  "R-NO-SUITABLE-CLIP": {
    human: "No clip on hand fits that slot.",
    kind: "refused",
  },
  "R-ATTEMPTS-EXHAUSTED": {
    human: "This reel has already been revised as many times as this change allows.",
    kind: "refused",
  },
  "R-LINT": {
    human: "The rewrite was produced but failed the on-screen text checks, so it was not kept.",
    kind: "refused",
  },
  "R-CONFLICT": {
    human: "Another revision of this reel is already in flight. Wait for it to land, then re-file.",
    kind: "refused",
  },
  "R-MISSING-STATE": {
    human: "The box is missing something this reel needs in order to be revised.",
    kind: "blocked",
  },
  "R-UNSPECIFIED": {
    human: "The engine stopped without naming a reason. This one needs a look.",
    kind: "blocked",
  },

  /* Colon codes — the PREFIX is what lands here; the detail is in the reason. */
  "NON-REVISABLE": {
    human: "This reel was built by an older renderer that did not record how it was made, so it cannot be re-cut. Re-rendering it fresh is the only route.",
    kind: "blocked",
  },
  "R-NEEDS-TARGET": {
    human: "That change needs you to point at the specific scene or clip that is wrong. Pick it from the failure list and re-file.",
    kind: "refused",
  },
  "R-INVALID-TARGET": {
    human: "The scene or clip named is not among this reel's failures. Pick one from its own list.",
    kind: "refused",
  },

  /* The command never got as far as the engine. */
  VERDICT_ALREADY_RECORDED: {
    human: "This decision was already recorded. Nothing was done twice.",
    kind: "blocked",
  },
  VERDICT_FILE_MISSING: {
    human: "The box could not find its own record of this decision.",
    kind: "blocked",
  },
  INVALID_VERDICT: {
    human: "The box did not recognise that decision.",
    kind: "blocked",
  },
  REQUEST_ID_PAYLOAD_MISMATCH: {
    human: "This looks like a re-send carrying different content, so it was refused rather than quietly overwriting the earlier decision. Re-file it from this card.",
    kind: "blocked",
  },

  /* ─── OPTION B · when the model WROTE a line and the engine would not take it.
   *
   * Grant ruled 2026-08-27 that Claude may write on-screen copy. The engine
   * re-checks every written line at plan time and refuses per target. These
   * three codes did not exist before that ruling, and without copy here they
   * render as raw engine text on the very card that is supposed to explain
   * them — the same gap NON-REVISABLE sat in for months.
   *
   * 🔑 THESE ARE NOT FAULTS. Each one is the gate doing its job on a line a
   * machine wrote, which is the entire safety argument for letting it write at
   * all. The copy says so, because "refused" reading as "broken" is what made
   * the truth-lock look like a failure in the first place. ─── */
  "R-AUTHORED-WIDENS-CLAIM": {
    human: "Claude's wording claimed more than the verified facts support, so it was not used. Nothing was changed. Ask again and it will try a narrower line.",
    kind: "refused",
  },
  "R-AUTHORED-TOO-LONG": {
    human: "Claude's wording was too long to read in the time that line is on screen, so it was not used. Nothing was changed.",
    kind: "refused",
  },
  "R-AUTHORED-NO-CHANGE": {
    human: "Claude came back with the line that is already on screen, so nothing was changed. If you want it different, say what is wrong with it.",
    kind: "refused",
  },
};

/*
 * How long a cursor must rest on a card before its reel is fetched. Long enough
 * that crossing the grid costs nothing; short enough that a deliberate hover
 * still feels immediate.
 */
export const HOVER_INTENT_MS = 220;

const refusalCopy = (item: ContentReviewItem) =>
  REFUSAL_COPY[(item.command?.refusal_code ?? "").trim()] ?? null;

/*
 * Ch71 — THE CODE STRINGS ARE NOT STABLE. Measured 2026-08-28: the box's
 * `decide` runs emit FREE-TEXT codes from a model, and one archetype produced
 * eight different spellings in one day (NOTE-WIDENS-CLAIM,
 * CLAIM-WIDENING-NOT-LICENSED, FACT-NOT-IN-REEL, ...). The map above can never
 * keep up with an open set, so an unmapped code must not surface RAW — and it
 * used to, on the chip ("Not made — NOTE-WIDENS-CLAIM"). The payload's own
 * `refusal_reason` sentence is high quality and always travels with the code,
 * so the unmapped path renders THAT, verbatim, under a generic header.
 * React renders it as a text node, so it is escaped by construction.
 */
const refusalRationale = (item: ContentReviewItem): string | null => {
  const reason = (item.command?.refusal_reason ?? "").trim();
  return reason || null;
};

/*
 * The condition the details link USED to be gated on. Kept as a named predicate
 * because the link is now unconditional and this still decides WHICH sentence it
 * shows — a refusal to act on, or an invitation to read the package.
 */
const isRefusedOrErrored = (item: ContentReviewItem): boolean =>
  item.command?.state === "refused" || item.errors.length > 0;

/*
 * THE VERSION LINE (Ch62 slice 2, designed with Grant).
 *
 * `template_version` is NOT a version. contentLineCore.ts:503 reads
 * `facts.template_version ?? "v1"` — every card in production shows the literal
 * fallback, and it can never change. Grant spotted it: "does v1 actually mean
 * anything? it doesnt update with new versions." It doesn't. `revision_number`
 * is the real counter and it was computed all along.
 *
 * Dots, not labelled chips — his call, and he was right: the causal line below
 * already says what changed, so labelled chips said it twice.
 * A first cut renders NO strip at all. Most cards are first cuts and must look
 * exactly as they did before this change.
 */
const revisionCount = (item: ContentReviewItem): number => {
  const n = item.revision_number ?? 0;
  return Number.isFinite(n) && n > 0 ? Math.min(Math.floor(n), 20) : 0;
};

/*
 * "You said X -> it changed Y" is the whole point of the loop: 34+ of Grant's
 * verdicts evaporated before the box could act on them. This line is the
 * visible proof that a note landed. The box fills changed_fields ONLY on a
 * revision render, so this appears exactly when it is meaningful.
 */
const changedSummary = (item: ContentReviewItem): string | null => {
  const fields = (item.changed_fields ?? []).filter(Boolean);
  if (fields.length === 0) return null;
  // A field path is a machine's noun. "scenes.scene-feature-2.on_screen_text"
  // is not something to read at a glance, and this line exists to be read at a
  // glance — so strip the container and spell the rest as words.
  const pretty = fields.map((f) =>
    f.replace(/^scenes\./, "").replaceAll("_", " ").replaceAll(".", " ").trim());
  return pretty.length <= 3
    ? pretty.join(" · ")
    : `${pretty.slice(0, 3).join(" · ")} +${pretty.length - 3} more`;
};

/*
 * THE DISTINGUISHING NAME, DERIVED — a bridge, not the fix.
 *
 * Every service explainer publishes the SAME `subject`: "how the free locator
 * works". service-reel.ts hardcodes one publicSubject for all seven presets, so
 * six visibly different reels arrive in the tab under one headline — which is
 * Grant's founding complaint ("basically all the same video") reappearing in the
 * surface built to solve it.
 *
 * The distinguishing name is already on every card. The box encodes the idea id
 * in `reel_id`:
 *
 *   service_explainer-service-remote-ebb7c60870   -> "remote"
 *   service_explainer-service-why-locator-b6f9…   -> "why locator"
 *
 * 🔴 THIS IS A SECONDARY LINE AND MUST STAY ONE. `subject` remains the headline.
 * Nothing may depend on parsing an id for correctness — the real fix is Grant's
 * seven authored subjects, and a derived line that looked authoritative would
 * quietly retire it.
 *
 * REFUSES rather than guesses: an id that does not decode renders NOTHING. Never
 * a raw id, never a placeholder. A line reading "property_tour-a3f9…" on nine
 * cards is worse than no line at all.
 */
const derivedName = (item: ContentReviewItem): string | null => {
  const reelId = (item.reel_id ?? "").trim();
  const template = (item.template ?? "").trim();
  if (!reelId || !template || !reelId.startsWith(`${template}-`)) return null;

  let rest = reelId.slice(template.length + 1);
  // The box appends a content hash. Drop it; it is identity, not language.
  rest = rest.replace(/-[0-9a-f]{6,}$/i, "");
  // Producers prefix the idea id with their own family ("service-how-free").
  // That word is already shown as the template chip, so it says nothing twice.
  rest = rest.replace(/^(service|property|neighborhood(-guide)?)-/, "");
  rest = rest.replace(/-/g, " ").trim();

  if (!rest) return null;
  // Anything still hex-shaped, or with no letters, is an id we failed to decode.
  if (!/[a-z]/i.test(rest) || /^[0-9a-f\s]+$/i.test(rest)) return null;
  return rest;
};

const refusalLabel = (item: ContentReviewItem): string => {
  const code = (item.command?.refusal_code ?? "").trim();
  // Ch71: a code is only worth printing if the map can also explain it in the
  // panel. An unmapped code is model free-text — meaningless to the reviewer
  // and unstable across runs — so the chip goes generic and the panel carries
  // the engine's own sentence instead.
  return code && REFUSAL_COPY[code]
    ? `Not made — ${code}`
    : "Not made — the engine declined";
};

const commandLabel = (item: ContentReviewItem): string | null => {
  if (!item.command) return null;
  if (commandWaitingForChild(item.command)) return "Applied — waiting for the new video";
  if (item.command.state === "applied") return "Applied";
  if (item.command.state === "conflict") return "Decision conflict — nothing changed";
  if (item.command.state === "refused") return refusalLabel(item);
  if (isNoteOnlyOutcome(item)) {
    // Deliberately says "Saved", not "Note saved": the dial can be submitted
    // with an empty note, and claiming words were kept when none were typed
    // would be a new small lie in the place we are removing one.
    return item.command.state === "exhausted"
      ? "Saved — the engine couldn’t re-render this reel"
      : "Saved — re-render unavailable for this reel";
  }
  if (item.command.state === "exhausted") return "Couldn’t apply — Admin review needed";
  return "Queued — waiting for Content engine";
};

const ContentReviewCard: React.FC<Props> = ({
  item,
  mobile,
  active = false,
  busy,
  downloading = false,
  onMarkPosted,
  onApprove,
  onReject,
  onSendBack,
  onDownload,
  unknownVerdict = null,
  onRetry,
}) => {
  const theme = useTheme();
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const stageRef = React.useRef<HTMLDivElement | null>(null);
  const videoLoadRef = React.useRef<Promise<string | null> | null>(null);
  const videoExpiresAtRef = React.useRef(0);
  const videoRefreshAttemptedRef = React.useRef(false);
  const [videoUrl, setVideoUrl] = React.useState<string | null>(null);
  /*
   * 🔴 DETAILS REPLACE THE VIDEO. THE CARD NEVER GROWS.
   *
   * Grant's design, and I built the wrong thing first: "we could have a View
   * details button that converts the card where the video is into a details
   * section, using the space on the card." I clamped and collapsed text in the
   * FOOTER instead, which still changed the footer's height — and a CSS grid row
   * is as tall as its tallest card, so one refused card gave every short card
   * beside it a dead gap. Clamping made that smaller. It did not make it stop.
   *
   * Everything explanatory now lives in a panel that occupies the STAGE, which
   * has a fixed aspect ratio. Every card in a row is the same height, always,
   * whatever it is carrying.
   */
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  /*
   * THE MEDIA'S OWN ASPECT RATIO, measured — not assumed.
   * null until the browser tells us; see the stage sx below for why.
   */
  const [mediaAspect, setMediaAspect] = React.useState<number | null>(null);
  /*
   * 🔴 ONLY THE VIDEO SETS THE STAGE. THE POSTER MUST NOT.
   *
   * Slice 1 took the ratio of whichever media reported first, and the poster
   * always wins that race (the video is preload="none"). But the poster is the
   * FRAME SHEET, which is four 360x640 frames hstacked — 1440x640, aspect 2.25 —
   * while the reel itself is 9:16. So an unplayed card rendered wide and a
   * played one rendered tall, and the grid had two different card heights for
   * the same kind of thing. Grant saw it immediately: "the size of the cards is
   * weird".
   *
   * The reel is what is being judged, so the reel's shape is the card's shape.
   * A wide frame sheet is letterboxed INSIDE it by objectFit:contain, which is
   * the correct subordinate treatment for a contact sheet.
   */
  const noteVideoAspect = React.useCallback((w: number, h: number) => {
    if (w > 0 && h > 0) setMediaAspect(w / h);
  }, []);
  const [posterUrl, setPosterUrl] = React.useState<string | null>(null);
  /*
   * IS A FRAME ON SCREEN RIGHT NOW? The `poster` ATTRIBUTE cannot answer this.
   * A browser shows a poster only until the first frame is decoded and never
   * again — so after one hover the card sat on whatever frame it stopped at,
   * and the queue turned into a wall of arbitrary mid-reel stills. Grant, on the
   * deployed tab: "it doesnt go back to the thumbnail when you stop hover."
   * This drives an overlay that restores the thumbnail on every pause.
   */
  const [playing, setPlaying] = React.useState(false);
  /*
   * PINNED — a click says "keep playing while I look away". Hover is a preview;
   * a click is intent, and intent should survive the cursor moving off the card.
   */
  const [pinned, setPinned] = React.useState(false);
  const [loadingVideo, setLoadingVideo] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  // 053:384 is the authority on what can still take a verdict — NOT which tab
  // the card happens to be rendered in. A qa_failed reel sits in the Rejected
  // corpus and is still fully actionable there.
  // 🔴 `!item.command` WAS THE SECOND HALF OF PROOF-0473. A card carrying ANY
  // command truth lost its controls, including the states migration 080 already
  // frees. `commandSlotReleased` is the rule; see its doc comment.
  const actionable = item.verdict === "pending"
    && (VERDICT_ELIGIBLE_STATUSES as readonly string[]).includes(item.status)
    && (!item.command || commandSlotReleased(item.command))
    && !unknownVerdict;
  const approved = item.status === "approved";
  const postedPlatforms = item.posted_platforms ?? [];
  const corpus = CORPUS_LABELS[item.status];
  /**
   * 🔴 PKT-0427 — THIS FLAG NOW CHANGES THE COPY. IT NO LONGER REMOVES ACCESS.
   *
   * It used to feed `disabled`, which meant Grant could not express an opinion
   * on ANY of the 11 packages in his live queue — `revision_capable` is false on
   * every one of them, including the two the box published today, because it
   * means "a script IR was captured AT RENDER TIME" and the property-reel path
   * never supplied one. A separate packet (PROOF-0422) is fixing that, and it
   * needs a release, a cutover and a re-render to land.
   *
   * 🔑 AND THE NOTE IS KEPT REGARDLESS. Migration 055 stores `review_note` on
   * `content_verdict_commands`, so Grant's words persist in his own database
   * whether or not the engine can ever act on them. Only the RE-RENDER fails.
   * Gating the whole control therefore threw away the one part that still
   * worked, and his one real note ("Text on screen needs work", 2026-07-25) was
   * discarded silently.
   *
   * 🔄 CORRECTED Ch72 (2026-08-29): this paragraph used to open "BUT THE NOTE
   * NEVER GOES TO THE BOX ANYWAY… 055 DELIBERATELY excludes it from the box
   * lease." **Migration 085 reversed that** and is applied in production: the
   * lease returns `review_note` at column 14 and the running box release reads
   * it. The argument for keeping this control reachable does not weaken — it
   * gets STRONGER, because the note now both persists AND reaches the engine on
   * every revisable package.
   *
   * ⇒ The control stays reachable; every surface that touches it says plainly
   *   that the re-render will not happen. Honest, not hidden — and it must be
   *   legible BEFORE he types, which is why there is a visible caption below
   *   rather than only a hover tooltip (there is no hover on the reel feed).
   */
  const revisionUnavailable = actionable && !item.revision_capable;
  const durableStatus = commandLabel(item);


  React.useEffect(() => {
    if (!item.frame_sheet_available) return;
    let active = true;
    void contentReviewService
      .media(item.id, "frame-sheet")
      .then((link) => {
        if (!active) return;
        setPosterUrl(link.url);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [item.frame_sheet_available, item.id]);

  const ensureVideo = React.useCallback(async () => {
    if (videoUrl && Date.now() < videoExpiresAtRef.current - 5000) return videoUrl;
    if (!item.video_available) return null;
    if (!videoLoadRef.current) {
      setLoadingVideo(true);
      videoLoadRef.current = contentReviewService
        .media(item.id, "video")
        .then((link) => {
          videoExpiresAtRef.current = Date.now() + link.expires_in_seconds * 1000;
          return link.url;
        })
        .catch(() => null)
        .finally(() => {
          setLoadingVideo(false);
          videoLoadRef.current = null;
        });
    }
    const url = await videoLoadRef.current;
    if (url) setVideoUrl(url);
    return url;
  }, [item.id, item.video_available, videoUrl]);

  const play = React.useCallback(async () => {
    const url = await ensureVideo();
    const video = videoRef.current;
    if (!url || !video) return;
    if (video.src !== url) video.src = url;
    // React assigns `muted` as a property only; browsers gate autoplay on the
    // element's muted state at call time, so assert it before every attempt.
    video.muted = muted;
    const playback = video.play();
    if (playback) await playback.catch(() => undefined);
  }, [ensureVideo, muted]);

  const pauseAndReset = React.useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  }, []);

  /*
   * HOVER INTENT — because a hover costs BYTES, not just a play() call.
   *
   * `preload="none"` means nothing is fetched until something asks the element to
   * play, so hover is the moment the reel is downloaded. Dragging the cursor across
   * a grid of fifteen cards on the way to somewhere else therefore started fifteen
   * downloads of ~10MB each. The signed URL is cached until it expires, so a SECOND
   * hover of the same card is free — the waste is entirely in cards the reviewer
   * passed over and never meant to watch.
   *
   * A short delay before the first byte spends egress only on a deliberate hover.
   * It is not a debounce on playback: once playing, behaviour is unchanged.
   */
  const hoverTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHoverIntent = React.useCallback(() => {
    if (hoverTimer.current === null) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  }, []);

  const playOnHoverIntent = React.useCallback(() => {
    cancelHoverIntent();
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null;
      void play();
    }, HOVER_INTENT_MS);
  }, [cancelHoverIntent, play]);

  const endHover = React.useCallback(() => {
    cancelHoverIntent();
    // A pinned card was clicked on purpose. Leaving must not stop it.
    if (pinned) return;
    pauseAndReset();
  }, [cancelHoverIntent, pauseAndReset, pinned]);

  // A card can unmount mid-hover (a tab switch, a refresh). Without this the
  // timer fires against a dead element and fetches a reel nobody is looking at.
  React.useEffect(() => cancelHoverIntent, [cancelHoverIntent]);

  const toggleMute = React.useCallback(() => {
    setMuted((current) => {
      const next = !current;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
    if (videoRef.current?.paused) void play();
  }, [play]);

  // The feed owns which reel is on screen (scroll position is authoritative and
  // testable); IntersectionObserver is not used here because it cannot be
  // exercised in every runtime we verify in.
  React.useEffect(() => {
    if (!mobile) return;
    if (active) void play();
    else pauseAndReset();
  }, [active, mobile, pauseAndReset, play]);

  const pillSx = mobile
    ? {
        borderRadius: 999,
        color: "rgba(255,255,255,0.92)",
        borderColor: "rgba(255,255,255,0.28)",
        bgcolor: "rgba(10,12,20,0.45)",
        backdropFilter: "blur(12px)",
        "&:hover": { bgcolor: "rgba(10,12,20,0.6)", borderColor: "rgba(255,255,255,0.4)" },
      }
    : {};

  const statusStack = (
    <Stack spacing={0.75} sx={mobile ? { alignItems: "center" } : undefined}>
      <Chip
        label={durableStatus}
        color={item.command?.state === "applied"
          ? "success"
          : ["conflict", "exhausted"].includes(item.command?.state ?? "")
            ? "error"
            : /*
               * A refusal is deliberately NOT "error". Nothing went wrong:
               * the engine did its job and said no. Colouring it red would
               * read as a breakage and send Grant looking for a fault that
               * does not exist.
               */
              item.command?.state === "refused"
              ? "info"
              : "warning"}
        /*
         * 🔴 A PAST OUTCOME MUST NOT OUTSHOUT A PENDING DECISION.
         *
         * "Applied" rendered as a filled full-width bar — the loudest element
         * on the card — while the two things Grant actually has to do, approve
         * and reject, sat beneath it as small icons. Grant: the green bar
         * "looks like a button and isn't". It is history; it now reads as
         * history. `outlined` keeps the colour (so "refused correctly" is still
         * legibly different from "broke") and drops the shout.
         *
         * The chip and the controls appearing TOGETHER is deliberate and is
         * PROOF-0473's fix — migration 080 freed the slot, so a card that
         * already carries an outcome can still take a new verdict. What was
         * wrong was the volume, not the coexistence.
         */
        variant={item.command?.state === "applied" ? "outlined" : "filled"}
        sx={{ minHeight: TOUCH_TARGET, ...(mobile ? { justifySelf: "center", backdropFilter: "blur(12px)" } : {}) }}
      />
    </Stack>
  );

  /*
   * THE VERSION LINE — SHARED BY BOTH LAYOUTS.
   *
   * It was desktop-only when it shipped. Grant asked about mobile FIRST — the
   * dots were his idea for the phone — and the mobile overlay rendered none of
   * it. A reviewer on a phone saw no version, no count and no note of what the
   * box changed, which is most of the reason the line exists.
   */
  /*
   * 🔴 SPLIT FROM `versionLine` 2026-08-21, AND THE SPLIT IS THE POINT.
   *
   * `versionLine` was one fragment carrying THREE things: the version dots, the
   * details link, and the changed-fields line. Moving 'the version line' into the
   * detail panel therefore silently took the DETAILS LINK off the card face — the
   * only way back into the panel. Caught in a browser, not by a type or a test:
   * a card rendered with no way to open it.
   *
   * The dots appear only on a revision, so on the face they were a per-card height
   * difference. They belong in the panel. The link must never leave the face.
   */
  const versionDots = (
    <>
  {revisionCount(item) > 0 && (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      data-testid="content-version-dots"
      aria-label={`version ${revisionCount(item) + 1} of ${revisionCount(item) + 1}`}
      sx={{ mt: 0.75 }}
    >
      {Array.from({ length: revisionCount(item) + 1 }).map((_, i) => {
        const current = i === revisionCount(item);
        return (
          <Box
            key={i}
            sx={{
    width: current ? 16 : 5,
    height: 5,
    borderRadius: 3,
    bgcolor: current ? "primary.main" : "text.disabled",
            }}
          />
        );
      })}
    </Stack>
  )}
    </>
  );

  const versionLine = (
    <>
  {
    /*
     * ONE line, fixed height — and it is THE REASON, in English.
     *
     * ContentPage's own test states why something must be here: "the reason is what
     * the second decision is MADE FROM… the buttons would come back and the sentence
     * explaining them would vanish." That requirement is RIGHT and this honours it.
     * What changes is WHICH sentence: it pinned the engine's 200-word paragraph;
     * this is the sentence a reviewer can act on, and the engine's verbatim text is
     * one click away in the panel, complete and unclamped.
     * ⇒ Strictly more is pinned than before: the explanation is beside the controls
     *   AND the full engine text is still reachable. Grant ruled this (option A).
     *
     * 🔴 2026-08-21 — THIS LINE IS NOW UNCONDITIONAL, AND THAT IS THE POINT.
     *
     * It used to render only on `refused || errors.length > 0`. Measured against
     * production: the six explainers published tonight are clean, so they had NO
     * link — and the details panel was therefore UNREACHABLE on exactly the cards
     * Grant is trying to review. The review surface was closed on the healthy path.
     * A card that cannot be opened cannot be reviewed, and "nothing went wrong" is
     * not a reason to withhold the caption, the clips and the QA result.
     *
     * The slot is always filled, so it also stops being one of the three footer
     * elements that toggle and give the grid five distinct card heights.
     */
    <Typography
      variant="caption"
      data-testid="content-details-open"
      data-kind={refusalCopy(item)?.kind ?? (isRefusedOrErrored(item) ? "unknown" : "healthy")}
      onClick={() => setDetailsOpen(true)}
      sx={{
        display: "block",
        mt: 0.75,
        cursor: "pointer",
        textDecoration: "underline",
        color: refusalCopy(item)?.kind === "blocked" ? "warning.main" : "text.secondary",
      }}
    >
      {isRefusedOrErrored(item)
        /*
         * Ch71 — an unmapped refusal code shows the ENGINE'S OWN SENTENCE here,
         * not a generic invitation to click. The codes are model free-text and
         * unstable; the rationale is the stable, readable part of the payload.
         * No rationale either -> the old generic line, unchanged.
         */
        ? refusalCopy(item)?.human
          ?? (item.command?.state === "refused" ? refusalRationale(item) : null)
          ?? "Why this was refused"
        : "Caption, clips and quality checks"}
    </Typography>
  }
  {changedSummary(item) && (
    /*
     * ⚠️ THIS STAYS ON THE FACE, AND THAT IS A DELIBERATE COST.
     *
     * Moving it (and the version dots) into the panel made every Review-tab card
     * exactly 700px — a real win — and broke SEVEN tests that pin them to the face,
     * including "shows the version dots on mobile, not only on desktop". That is
     * Ch62 work designed WITH Grant: "you said X -> it changed Y" is the visible
     * proof a note landed, after 34+ of his verdicts evaporated.
     *
     * A uniform grid is not worth silently deleting the feature that tells him the
     * loop works. So a revised card is allowed to be taller than a first cut: it
     * genuinely carries more, and that difference is information rather than noise.
     */
    <Typography
      variant="caption"
      data-testid="content-changed-summary"
      color="text.secondary"
      sx={{ display: "block", mt: 0.75 }}
    >
      Changed <Box component="span" sx={{ color: "text.primary" }}>{changedSummary(item)}</Box>
    </Typography>
  )}
    </>
  );

  /*
   * The detail face. Same box as the video, so opening it costs no height.
   * The engine's own paragraph is here IN FULL — migration 079 widened that
   * column so it would arrive whole, and it still does. It is simply no longer
   * setting the height of a review card.
   */
  const detailPanel = (
    <Box
      data-testid="content-detail-panel"
      /*
       * position/inset are INLINE, not in `sx`. They are the whole guarantee that
       * opening this panel costs the card no height — and emotion classes are not
       * readable through getComputedStyle in jsdom, so an `sx` version could only
       * ever be asserted against a mirror. A mutation letting the panel go static
       * SURVIVED that mirror earlier today. One source of truth, assertable.
       */
      style={{ position: "absolute", inset: 0 }}
      sx={{
        zIndex: 3,
        bgcolor: "background.paper",
        p: 1.5,
        overflowY: "auto",
        display: "grid",
        gap: 1,
        alignContent: "start",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary">Details</Typography>
        <Button size="small" onClick={() => setDetailsOpen(false)} sx={{ minWidth: 0, px: 1 }}>Close</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {item.template} · {item.reel_id.slice(0, 12)}
      </Typography>
      {/* Moved OFF the card face so they stop setting per-card height. Nothing is
          lost: the face carries the glanceable half (a chip, "revised N×") and the
          full text is here, one click away on every card. */}


      {!isRefusedOrErrored(item) && (
        /*
         * 🔴 WHAT A HEALTHY CARD'S PANEL SAYS.
         *
         * Until 2026-08-21 this panel was unreachable unless a card was refused,
         * so a clean card opened to nothing. It now opens, and it must have
         * something true to say.
         *
         * ⚠️ SCOPE — UPDATED Ch76. An earlier version of this comment claimed
         * the item "carries NO caption and NO clip list ... never crosses the
         * mirror wire". That became FALSE when PKT-0602 / migration 089 landed
         * `scenes[]` (beat_role · caption · clip_id · offset_sec) on every
         * newly published package — and the stale sentence is a big part of
         * why nobody built the beats panel. The beats now render below
         * (content-detail-beats). What still does NOT cross the wire: raw clip
         * media and cue audio.
         */
        <Stack spacing={0.75} data-testid="content-detail-context">
          <Typography variant="body2" color="text.primary">
            {revisionCount(item) > 0
              ? `Version ${revisionCount(item) + 1} of this reel.`
              : "First cut — this reel has not been revised."}
          </Typography>
          {item.parent_reel_id && (
            <Typography variant="caption" color="text.secondary" data-testid="content-detail-parent">
              Revised from {item.parent_reel_id.slice(0, 12)}
            </Typography>
          )}
          {(item.q_codes ?? []).length > 0 && (
            <Typography variant="caption" color="text.secondary" data-testid="content-detail-qcodes">
              Quality flags: {(item.q_codes ?? []).join(" · ")}
            </Typography>
          )}
          {!item.revision_capable && item.capability_reason && (
            <Typography variant="caption" color="text.secondary">
              {item.capability_reason}
            </Typography>
          )}
        </Stack>
      )}

      {/* Ch76 · P1 BEATS PANEL — the first render site for scenes[] (PKT-0602 /
          migration 089). One line per beat, reel order: `beat_role · caption`.
          ABSENT is the normal state until the box republishes older packages,
          so the empty case says so instead of pretending the reel has no
          structure. */}
      {beatLines(item.scenes).length > 0 ? (
        <Stack spacing={0.5} data-testid="content-detail-beats">
          <Typography variant="caption" color="text.secondary">Beats</Typography>
          {beatLines(item.scenes).map((b) => (
            <Typography
              key={b.id}
              variant="caption"
              color="text.primary"
              data-testid="content-detail-beat-line"
              sx={{ display: "block", lineHeight: 1.5 }}
            >
              {b.text}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography
          variant="caption"
          color="text.disabled"
          data-testid="content-detail-beats-empty"
        >
          No beat list on this package — published before the box shipped scenes (migration 089).
        </Typography>
      )}
      {item.command?.state === "refused" && refusalCopy(item) && (
        /*
         * 🔴 ONE LINE, NOT THREE. Grant, on the deployed card: the rows have "extra
         * space" — a grid row is as tall as its tallest card, and a refused card was
         * carrying FOUR stacked blocks (status bar · kind chip · this sentence · the
         * engine paragraph) against two on a normal card. That card set the height of
         * its whole row and every short card beside it inherited the dead space.
         *
         * The kind chip and this sentence said the same thing twice, so the chip is
         * gone and the KIND now rides this line's colour and its testid. The engine
         * paragraph is collapsed by default rather than clamped to two lines — still
         * one click away, still complete, no longer setting anyone's row height.
         */
        <Typography
          variant="body2"
          data-testid="content-refusal-human"
          data-kind={refusalCopy(item)!.kind}
          sx={{
            color: refusalCopy(item)!.kind === "blocked" ? "warning.main" : "text.primary",
            textAlign: mobile ? "center" : "left",
          }}
        >
          {refusalCopy(item)!.human}
        </Typography>
      )}
      {item.command?.state === "refused" && !refusalCopy(item) && refusalRationale(item) && (
        /*
         * Ch71 — the generic header for an UNMAPPED code. Deliberately NOT
         * `content-refusal-human` and NOT an entry in REFUSAL_COPY: the map
         * must never become a catch-all handing unknown codes copy not written
         * for them (pinned by the fall-through test). This header just frames
         * the engine's verbatim sentence, which renders in full below.
         */
        <Typography
          variant="body2"
          data-testid="content-refusal-generic-header"
          sx={{ color: "text.primary", textAlign: mobile ? "center" : "left" }}
        >
          The engine declined this one. Its own reason, in full:
        </Typography>
      )}
      {item.command?.state === "refused" && item.command.refusal_reason && (
        /*
         * 🔴 THE POINT OF THE WHOLE PACKET. Steps 1-3 of this change stop
         * the card lying; only this line tells him WHY. It is the engine's
         * own sentence, unedited and untruncated — migration 079 gave it a
         * 2000-character column precisely so it would arrive whole, and
         * marks its own cut if it ever does not.
         */
        <Typography
          variant="caption"
          data-testid="content-refusal-reason"
          sx={{
            color: "text.secondary",
            whiteSpace: "pre-wrap",
            textAlign: mobile ? "center" : "left",
            /*
             * Ch72 — NOT CLAMPED, AND NO LONGER CLAIMING TO BE.
             *
             * #440 line-clamped this paragraph and made it click-to-expand.
             * #443 replaced that with collapse-by-default — the panel replaces
             * the video, so a card never grows and the sentence can simply be
             * whole — and removed the clamp CSS. What it did NOT remove was
             * `data-clamped="true"` (a hardcoded literal reflecting no state)
             * and `cursor: "pointer"` (on an element with no onClick).
             *
             * Both were removed here because both were lies of the same kind
             * this card exists to end: an artifact asserting behaviour the code
             * does not implement. The cursor promised Grant a click that did
             * nothing; the attribute promised the next test author a clipping
             * rule that was already gone.
             *
             * Measured 2026-08-29: production refusals now run 832 and 784
             * characters (a per-scene evidence tail the 162-char archetype did
             * not have). They render WHOLE here, which is the behaviour the
             * pinned test asserts — so there is nothing left for a clamp to do.
             */
          }}
        >
          {item.command.refusal_reason}
        </Typography>
      )}
    </Box>
  );

  const controls = (
    /*
     * THREE CONTROLS, THREE INTENTS — Grant's own sentence, in order:
     * "check/x icon for approve / reject and actual review button to
     *  submit notes to queue for re-render".
     *
     * ✓ Approve ("P") is a single tap that carries no note and opens no
     *   sheet — an approval runs zero operations (revisionEngine.ts:36),
     *   so words attached to it are discarded by the engine. Grant lost a
     *   real correction that way on 2026-07-25. It cannot happen here.
     * ✗ Reject ("H") is SLICE B, and it is the SAME shape on purpose: one
     *   tap, no sheet, no note. H is `{ operations: [], max_attempts: 0,
     *   stop_code: "R-HOLD" }` (revisionEngine.ts:37) — every word on it
     *   would be discarded exactly the way the approval's was. A reject
     *   sheet with a "say why" box would have rebuilt that defect under a
     *   new label. The reel itself is what the Rejected corpus teaches
     *   from; it lands in `parked`, which that tab already spans.
     * "Review" opens the only sheet that takes a note, and only alongside
     *   a revision dial that actually queues a re-render.
     */
    <Box sx={{ display: "grid", gridTemplateColumns: "auto auto 1fr", gap: 1, alignItems: "stretch" }}>
      {/*
        🔴 PKT-0427 — THE HONEST WARNING, AND IT IS VISIBLE BEFORE HE TYPES.
        A Tooltip alone would not do: the mobile reel feed is the surface
        Grant actually reviews on and it has no hover. Spanning the whole
        grid row keeps it above the controls without re-flowing them.
      */}
      {revisionUnavailable && (
        <Typography
          variant="caption"
          sx={{
            gridColumn: "1 / -1",
            fontWeight: 600,
            lineHeight: 1.35,
            color: mobile ? "rgba(255,255,255,0.88)" : "warning.main",
          }}
        >
          Re-rendering isn’t available for this reel yet — your note is saved either way.
        </Typography>
      )}
      <Button
        variant="contained"
        color="success"
        aria-label="Approve"
        disabled={busy}
        onClick={() => onApprove(item)}
        sx={{
          minWidth: TOUCH_TARGET,
          minHeight: TOUCH_TARGET,
          px: 1.5,
          ...(mobile ? { borderRadius: 999, boxShadow: "none" } : {}),
        }}
      >
        <CheckIcon />
      </Button>
      {/*
        🔴 DELIBERATELY NOT GATED ON `revision_capable`, and not on
        `sendBackUnavailable` either. That flag is the box's answer to
        "can I build a revision of this?" — a reject is the decision that
        no revision should ever be attempted, so gating it on the ability
        to revise would make the packages least able to be revised, the
        worst ones, the only ones you cannot reject. Exactly backwards.
        Eligibility is already handled by `actionable` (053:384).
      */}
      <Button
        variant="outlined"
        color="error"
        aria-label="Reject"
        disabled={busy}
        onClick={() => onReject(item)}
        sx={{
          minWidth: TOUCH_TARGET,
          minHeight: TOUCH_TARGET,
          px: 1.5,
          ...(mobile
            ? {
                borderRadius: 999,
                color: "rgba(255,255,255,0.92)",
                borderColor: "rgba(255,120,120,0.45)",
                bgcolor: "rgba(10,12,20,0.45)",
                backdropFilter: "blur(12px)",
                "&:hover": { bgcolor: "rgba(10,12,20,0.6)" },
              }
            : {}),
        }}
      >
        <RejectIcon />
      </Button>
      <Tooltip
        title={revisionUnavailable
          ? item.capability_reason
            || "This package cannot be re-rendered yet. Your note is still saved."
          : ""}
      >
        <Box component="span" sx={{ display: "flex" }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ReviewIcon />}
            /*
             * 🔴 PKT-0427 — `disabled` is `busy` ALONE now. It must never
             * take `revisionUnavailable` back: that is the gate that left
             * Grant unable to say anything about any of his 11 packages,
             * and the note it was blocking is stored (migration 055) and
             * leased to the box (migration 085), and is unaffected by the
             * box's inability to revise this particular reel.
             */
            aria-label={revisionUnavailable
              ? "Review — note saved, no re-render yet"
              : "Review"}
            disabled={busy}
            onClick={() => onSendBack(item)}
            sx={{ flex: 1, minHeight: TOUCH_TARGET, ...pillSx }}
          >
            Review
          </Button>
        </Box>
      </Tooltip>
    </Box>
  );

  return (
    <Box
      component="article"
      aria-label={`${item.subject} review card`}
      onMouseEnter={mobile ? undefined : playOnHoverIntent}
      onMouseLeave={mobile ? undefined : endHover}
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: mobile ? "#05070D" : "background.paper",
        borderRadius: { xs: 0, sm: 3 },
        border: { xs: 0, sm: `1px solid ${theme.palette.divider}` },
        height: mobile ? "100%" : undefined,
        scrollSnapAlign: mobile ? "start" : undefined,
        scrollSnapStop: mobile ? "always" : undefined,
        flexShrink: mobile ? 0 : undefined,
        boxShadow: mobile ? "none" : theme.shadows[5],
        isolation: "isolate",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": mobile ? undefined : {
          transform: "translateY(-3px)",
          boxShadow: theme.shadows[10],
        },
      }}
    >
      <Box
        ref={stageRef}
        role="group"
        aria-label={`${item.subject} unobstructed video player`}
        /*
         * aspect-ratio is set INLINE, not through `sx`. Emotion classes are not
         * readable via getComputedStyle in jsdom, so a test asserting this stage
         * matches its media could only ever check a mirror of the value — and a
         * mirror passes even when the real style is hardcoded. Found by a forced
         * mutation that SURVIVED. Inline keeps one source of truth and makes the
         * assertion touch the thing that actually sizes the box.
         */
        style={mobile ? undefined : { aspectRatio: String(mediaAspect ?? 9 / 16) }}
        sx={{
          position: "relative",
          width: "100%",
          height: mobile ? "100%" : undefined,
          /*
           * 🔴 THE STAGE MATCHES THE MEDIA. IT USED TO ASSUME 9/16.
           *
           * Everything the box renders is not portrait. A review artifact that
           * is a wide filmstrip was being poured into a hardcoded 9:16 box with
           * objectFit "contain", so roughly 60% of every card was black bars and
           * only three cards fit on a 1491px screen. Measured on the live tab,
           * 2026-08-21.
           *
           * We now use the ratio the browser reports (video metadata, or the
           * poster's natural size — whichever arrives first) and fall back to
           * 9/16 only while nothing has loaded yet, so a portrait reel is still
           * portrait and nothing regresses. Clamped both ways: a very tall reel
           * cannot push the card past 640px, and a very wide one still gets
           * enough height to be judged.
           */
          maxHeight: mobile ? undefined : 640,
          minHeight: mobile ? undefined : 220,
          bgcolor: "#05070D",
          overflow: "hidden",
        }}
      >
        {posterUrl && !playing && (
          /*
           * 🔴 SHOWN WHENEVER NOTHING IS PLAYING — not only before first load.
           *
           * The condition used to be `!videoUrl`: correct exactly once, then the
           * thumbnail was gone forever and every paused card showed a frozen
           * mid-reel frame. Restoring the poster is what makes a queue scannable,
           * because the frame sheet is the shot someone CHOSE.
           *
           * `pointerEvents: none` is load-bearing: the <video> sits underneath, so
           * clicks (pin, mute, native controls) must pass straight through.
           */
          <Box
            component="img"
            data-testid="content-poster-overlay"
            src={posterUrl}
            alt=""
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: mobile ? "cover" : "contain",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        )}
        <Box
          component="video"
          ref={videoRef}
          controls={!mobile}
          muted={muted}
          loop
          playsInline
          preload="none"
          poster={posterUrl ?? undefined}
          aria-label={`${item.subject} video preview`}
          onLoadedMetadata={(event: React.SyntheticEvent<HTMLVideoElement>) =>
            noteVideoAspect(event.currentTarget.videoWidth, event.currentTarget.videoHeight)}
          onPlaying={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onClick={(event: React.MouseEvent<HTMLVideoElement>) => {
            if (!event.currentTarget.currentSrc) { setPinned(true); void play(); return; }
            if (mobile) { toggleMute(); return; }
            // Desktop: click toggles the pin. Unpinning while the cursor is still
            // on the card leaves it playing — leaving the card then stops it, which
            // is the normal hover behaviour resuming, not a second rule.
            setPinned((current) => {
              if (current) return false;
              void play();
              return true;
            });
          }}
          onError={() => {
            if (videoRefreshAttemptedRef.current) return;
            videoRefreshAttemptedRef.current = true;
            videoExpiresAtRef.current = 0;
            setVideoUrl(null);
            void ensureVideo().then((url) => {
              if (url && videoRef.current) videoRef.current.src = url;
            });
          }}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: mobile ? "cover" : "contain",
            cursor: "pointer",
            zIndex: 1,
          }}
        />
        {detailsOpen && detailPanel}
        {mobile && (
          <Box
            component="button"
            type="button"
            aria-label={muted ? "Unmute video" : "Mute video"}
            onClick={toggleMute}
            sx={{
              // Clears the translucent app bar + floating queue pills above the reel.
              position: "absolute",
              top: "calc(env(safe-area-inset-top, 0px) + 108px)",
              right: 12,
              zIndex: 2,
              display: "grid",
              placeItems: "center",
              width: 44,
              height: 44,
              border: "1px solid rgba(255,255,255,0.22)",
              borderRadius: "50%",
              bgcolor: "rgba(10,12,20,0.45)",
              backdropFilter: "blur(12px)",
              color: "rgba(255,255,255,0.9)",
              cursor: "pointer",
              p: 0,
            }}
          >
            {muted ? <MutedIcon fontSize="small" /> : <UnmutedIcon fontSize="small" />}
          </Box>
        )}
        {!posterUrl && !videoUrl && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: "rgba(255,255,255,.75)",
              pointerEvents: "none",
            }}
          >
            {loadingVideo
              ? <CircularProgress color="inherit" />
              : <PlayIcon sx={{ fontSize: 64 }} />}
          </Box>
        )}
      </Box>

      <Box
        aria-label={`${item.subject} review controls`}
        sx={mobile
          ? {
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              px: 2,
              pt: 5,
              // Sits above the floating nav pill (14px inset + 60px tall).
              pb: "calc(env(safe-area-inset-bottom, 0px) + 90px)",
              display: "grid",
              gap: 1.25,
              background: "linear-gradient(180deg, rgba(5,7,13,0) 0%, rgba(5,7,13,0.62) 100%)",
            }
          : {
              p: 2.25,
              display: "grid",
              gap: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
      >
        {!mobile && (
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            /*
             * 🔴 nowrap, MEASURED. With wrap, a long label ("Not made —
             * R-NO-ELIGIBLE-COPY") pushed this row to two lines and added ~26px to
             * that card alone — so the row that was supposed to REMOVE height
             * variance was quietly creating it. One line always; the chip that
             * cannot fit truncates, and its full text is in the panel below.
             */
            flexWrap="nowrap"
            alignItems="center"
            sx={{ overflow: "hidden", "& .MuiChip-root": { maxWidth: "100%" } }}
          >
            <Chip
              size="small"
              label={corpus?.label ?? humanStatus(item.status)}
              color={approved ? "success" : corpus ? "warning" : "default"}
              variant={corpus ? "outlined" : "filled"}
            />
            {durableStatus && (
              /*
               * 🔴 THE LAST HEIGHT TOGGLE, REMOVED — measured, not guessed.
               *
               * Card heights on the deployed tab: 817 / 823 / 859 / 875 / 917 —
               * five distinct, 100px spread, ALL of it footer. Three elements
               * toggled independently; the details link became unconditional in
               * the previous commit, and this is the second: a full-width Chip in
               * a stretch Stack at minHeight 44 plus an 8px gap, appearing and
               * vanishing per card. It matched the measured 177→235 footer delta.
               *
               * Reserving space for it was considered and REJECTED for a good
               * reason: it makes every card the tallest card and hands a simple
               * card ~100px of deliberate blank space, which is the thing being
               * complained about, redistributed rather than removed. So the chip
               * MOVES into a row that already exists and already wraps — costing
               * no new vertical block at all.
               *
               * It is history, not a control: `size="small"` here, and it keeps
               * its colour so "refused correctly" still reads differently from
               * "broke". Mobile keeps the full-size chip — a thumb needs the
               * target, and the mobile reel is one card per screen, so a varying
               * height costs nothing there.
               */
              <Chip
                size="small"
                data-testid="content-durable-status-chip"
                label={durableStatus}
                color={item.command?.state === "applied"
                  ? "success"
                  : ["conflict", "exhausted"].includes(item.command?.state ?? "")
                    ? "error"
                    : item.command?.state === "refused"
                      ? "info"
                      : "warning"}
                variant="outlined"
              />
            )}
            {item.errors.length > 0 && (
              <Chip
                size="small"
                color="error"
                label={`${item.errors.length} issue${item.errors.length === 1 ? "" : "s"}`}
              />
            )}
            {approved && postedPlatforms.length > 0 && (
              /* PKT-0612: the ledger says this reel went out. Absent key =
                 unknown, and unknown renders NOTHING rather than "not posted". */
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<PostedIcon />}
                data-testid="content-posted-chip"
                label={`Posted · ${postedPlatforms.join(", ")}`}
              />
            )}
          </Stack>
        )}

        {!mobile && (
          <Box>
            {/*
              * 🔴 ONE LINE EACH, MEASURED ON A REAL BROWSER.
              *
              * Card heights in the harness: title block 70 / 88 / 103px depending
              * on whether the subject or the template line wrapped. A wrapping
              * headline is a per-card height difference, and a grid row is as tall
              * as its tallest card — so one long subject inflated every short card
              * beside it. Clamping to one line with an ellipsis removes the
              * variance WITHOUT reserving blank space (the fix that was rejected
              * for making every card the tallest card).
              *
              * `title` keeps the full text reachable on hover, and the panel — now
              * openable on every card — carries the untruncated version.
              */}
            <Typography variant="h6" fontWeight={800} lineHeight={1.2} noWrap title={item.subject}>
              {item.subject}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
              {item.template}
              {derivedName(item) && (
                <Box
                  component="span"
                  data-testid="content-derived-name"
                  sx={{ color: "text.primary" }}
                >
                  {` · ${derivedName(item)}`}
                </Box>
              )}
              {revisionCount(item) > 0 && ` · revised ${revisionCount(item)}×`}
            </Typography>
            {versionDots}
            {versionLine}
            {corpus && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                {corpus.help}
              </Typography>
            )}
            {item.errors.length > 0 && (
              /*
               * ⚠️ ALSO STAYS ON THE FACE, for the same reason as the changed line.
               * Moving it won ~22px of height uniformity and broke the test that
               * pins a seal failure being READABLE on the card. An error a reviewer
               * has to click to discover is an error they will not read.
               */
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {item.errors.join(" · ")}
              </Typography>
            )}
          </Box>
        )}

        {unknownVerdict ? (
          <Stack spacing={1}>
            <Chip
              label="Status unknown — retry safely"
              color="warning"
              sx={{ minHeight: TOUCH_TARGET, ...(mobile ? { justifySelf: "center", backdropFilter: "blur(12px)" } : {}) }}
            />
            <Button
              variant="contained"
              disabled={busy}
              onClick={() => onRetry(item)}
              sx={{ minHeight: TOUCH_TARGET, ...(mobile ? { borderRadius: 999 } : {}) }}
            >
              Retry decision
            </Button>
          </Stack>
        ) : durableStatus ? (
          /*
           * 🔴 PROOF-0473 DEFECT 2 — THE STATUS AND THE CONTROLS ARE NO LONGER
           * MUTUALLY EXCLUSIVE.
           *
           * This branch used to RETURN, so a card carrying any command truth
           * showed its chip WHERE THE BUTTONS WOULD BE and offered nothing to
           * click. For a refusal that was correct until migration 080: the
           * database answered `conflict` to a second verdict, so re-arming
           * would have produced "Decision conflict — nothing changed" forever.
           *
           * 080 IS APPLIED IN PRODUCTION (2026-08-18). The slot is free and the
           * screen is the only thing still saying no. Confirmed on Grant's tab:
           * "Not made — R-NO-ELIGIBLE-COPY" with the full 162-character reason
           * and NOTHING TO CLICK.
           *
           * The reason keeps rendering — that part was always right, and it is
           * what the second decision is made FROM. `commandSlotReleased` is the
           * single place that decides which states the database will accept a
           * new verdict for.
           */
          <Stack spacing={1} sx={mobile ? { alignItems: "center" } : undefined}>
            {mobile && versionDots}
            {mobile && versionLine}
            {/* Desktop shows this in the chip row above — see the durable-status
                chip there. Rendering it twice would say the outcome twice AND
                restore the height toggle it was moved to remove. */}
            {mobile && statusStack}
            {item.command?.child_ready === true && item.command.child_package_id ? <Typography variant="body2">
              New video {item.command.child_package_id} is ready in Review. This card still shows the original video.
            </Typography> : null}
            {actionable ? controls : null}
          </Stack>
        ) : actionable ? (
          <Stack spacing={1} sx={mobile ? { alignItems: "center" } : undefined}>
            {mobile && versionDots}
            {mobile && versionLine}
            {controls}
          </Stack>
        ) : approved ? (
          /* THE PAYOFF SURFACE. The whole machine exists so this button works.
             PKT-0612 adds the second half of the payoff: once the human has
             posted the file, "Mark as posted" records the act so the ledger is
             fed by the click and not by memory. Manual by ruling; the sheet
             cannot publish. */
          <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
            <Tooltip title={item.video_available ? "" : "No video was published for this package."}>
              <Box component="span" sx={{ display: "flex", flex: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                  aria-label={item.video_available ? "Download video" : "Download unavailable"}
                  disabled={downloading || !item.video_available}
                  onClick={() => onDownload(item)}
                  sx={{ flex: 1, minHeight: TOUCH_TARGET, ...(mobile ? { borderRadius: 999, boxShadow: "none" } : {}) }}
                >
                  {downloading ? "Saving…" : "Download"}
                </Button>
              </Box>
            </Tooltip>
            {onMarkPosted && (
              <Button
                variant={postedPlatforms.length > 0 ? "text" : "outlined"}
                color="success"
                startIcon={<PostedIcon />}
                aria-label={postedPlatforms.length > 0 ? "Record another platform" : "Mark as posted"}
                disabled={busy}
                onClick={() => onMarkPosted(item)}
                sx={{ flex: 1, minHeight: TOUCH_TARGET, ...(mobile ? { borderRadius: 999, bgcolor: "rgba(10,12,20,0.45)", backdropFilter: "blur(12px)" } : {}) }}
              >
                {postedPlatforms.length > 0 ? "Posted" : "Mark as posted"}
              </Button>
            )}
          </Stack>
        ) : (
          /* Corpus history: readable, never deletable, and honest about why. */
          <Chip
            label={corpus?.label ?? "Review unavailable"}
            color={corpus ? "warning" : "default"}
            variant="outlined"
            sx={{ minHeight: TOUCH_TARGET, ...(mobile ? { justifySelf: "center", backdropFilter: "blur(12px)" } : {}) }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ContentReviewCard;
